import { Intercom, shutdown, update } from '@intercom/messenger-js-sdk';
import { useCommon } from 'src/domains/common/Common';
import { TrpcClient } from 'src/appState/TrpcClient';
import { useEffect } from 'react';
import { observer } from 'src/utils/mobx-react';
import { autorun } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { UsersState } from 'src/domains/players/state/UsersState';
import { AccountState } from 'src/domains/players/shared/Types';

export const IntercomChatDefault = async (
    trpcClient: TrpcClient,
    userId: number,
    email: string | null | undefined,
    name: string | undefined,
    createdDate: string | undefined,
    appId: string,
    region: string,
    apiBase: string
): Promise<void> => {
    const userIdString = userId.toString();
    const userEmail = email === null || email === undefined ? '' : email;
    const createdAt = DateTime.from(createdDate)?.unixSeconds();
    const hashFromServer = await trpcClient.client.intercomHashRouter.getHash.mutate({
        userId: userIdString,
    });

    update({
        app_id: appId,
        user_id: userIdString,
        name: name,
        email: userEmail,
        created_at: createdAt,
        user_hash: hashFromServer,
        region,
        api_base: apiBase,
    });
};

export const IntercomChat = observer('IntercomChat', () => {
    const common = useCommon();
    const accountState = AccountState.get(common);
    const { trpcClient, session } = common;
    const config = ConfigComponents.get(common).config;
    const usersState = UsersState.get(common);

    const { intercomChatAppId, intercomChatRegion, intercomChatApiBase } = config;

    const account = accountState.account?.basicDataReady;
    const createdAt = usersState.walletData.valueReady?.createdAt;

    useEffect((): (() => void) => {
        const dispose = autorun(async () => {
            if (session.isAuthorized && account !== null && account !== undefined) {
                try {
                    await IntercomChatDefault(
                        trpcClient,
                        account.id,
                        account.email,
                        account.name,
                        createdAt,
                        intercomChatAppId,
                        intercomChatRegion,
                        intercomChatApiBase
                    );
                } catch (error) {
                    console.error('Error initializing Intercom chat:', error);
                }
            } else {
                shutdown();
                update({
                    app_id: intercomChatAppId,
                    region: intercomChatRegion,
                    api_base: intercomChatApiBase,
                });
            }
        });

        return dispose;
    }, [session.isAuthorized, account?.id, createdAt]);

    Intercom({
        app_id: intercomChatAppId,
        region: intercomChatRegion,
        api_base: intercomChatApiBase,
    });

    return null;
});
